import React, { useCallback, useMemo } from 'react';

import classNames from 'clsx';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { ButtonThemeService, THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Segment } from '../../services/analytics-services/segment';
import { EventSourceStrings } from '../../tracking/tracking-constants';
import { TEST_LABEL_BUTTON_CLICKED } from '../../constants/analytics-events';
import { CREATE_TEST_LABEL } from '../../demos/demo-orders';
import Global from '../../store/global-selectors';

import { ComponentStates } from '../../enums/component-states';
import { useFulfillmentPlannerContext } from '../../fulfillment-planner/context';
import { CswPrimaryActionPropType } from '../../prop-types/csw-primary-action';
import { LabelPurchaseButton } from '../label-purchase-button';
import { OtherLabelActionButton } from './other-label-action-button';
import { RATE_BROWSER, CUSTOMERS_CREATE_LABEL } from '../../constants/fulfillment-plan-ids';
import { useCurrentCostReviewTrackingId } from '../../fulfillment-planner/hooks/useCurrentCostReviewTrackingId';

import style from './multiaction-label-button.module.less';
import { emptyArray } from 'ui-components/default-props/empty';

const messages = defineMessages({
  printLabelButtonOnlyText: {
    description: 'CSW primary action button print label only',
    defaultMessage: 'Print Label',
    id: 'H5ibQ9',
  },
});

const passthroughGuardAction = (proceed) => proceed();

export const MultiactionLabelButton = ({
  defaultPrimaryActionId = '',
  primaryActions = emptyArray,
  disabled = false,
  theme = THEME_CONSTANTS.primary,
  buttonClassName,
  buttonContainerClassName,
  buttonLabelClassName,
  disabledTooltipMessage,
  isLightTheme = false,
  guardAction = passthroughGuardAction,
}) => {
  const dispatch = useDispatch();
  const sellerId = useSelector(Global.selectSellerSellerId);
  const defaultPrimaryAction = primaryActions.find((primaryAction) => primaryAction.id === defaultPrimaryActionId) || primaryActions[0];
  const isDemoOrder = defaultPrimaryActionId === CREATE_TEST_LABEL;
  const otherActions = primaryActions.filter((primaryAction) => primaryAction.id !== defaultPrimaryAction.id && !isDemoOrder);
  const {
    uiRestrictions,
    parentComponentName,
    cswTypeId,
    fulfillmentPlanId,
    insuranceProvider,
    isReturn,
    serviceIdAndSellerLabelProviderId,
    availableDutiesOptions,
    onSelectedDutyOptionChange,
    otherShippingOptions,
    handleUpdateInsurancePreference,
  } = useFulfillmentPlannerContext();

  const printLabelButtonOnly = uiRestrictions?.labelAction?.componentState === ComponentStates.PrintOnly;

  const defaultPrimaryActionBtnLabelTxt = printLabelButtonOnly ? messages.printLabelButtonOnlyText : defaultPrimaryAction.translation;
  const eventTriggerSource = `Create Label button-${parentComponentName || cswTypeId}`;
  const { setCurrentAnalyticsSourceTrackingId } = useCurrentCostReviewTrackingId(eventTriggerSource);

  const labelMetadata = useMemo(() => {
    return {
      insuranceProviderName: insuranceProvider?.name,
      isReturn,
      serviceId: serviceIdAndSellerLabelProviderId?.serviceId,
    };
  }, [insuranceProvider, isReturn, serviceIdAndSellerLabelProviderId]);

  const onDefaultAction = useCallback(() => {
    guardAction(() => {
      setCurrentAnalyticsSourceTrackingId(eventTriggerSource);
      // Track test label button click with Segment if it's a test label
      if (isDemoOrder) {
        Segment.trackEvent(TEST_LABEL_BUTTON_CLICKED, {
          source: EventSourceStrings.ORDER_DETAILS_CSW,
          sellerId,
          fulfillmentPlanId,
        });
      }
      dispatch(defaultPrimaryAction.getActionFromFulfillmentPlanId(fulfillmentPlanId, labelMetadata));
      if (!isDemoOrder) {
        handleUpdateInsurancePreference();
      }
    });
  }, [
    guardAction,
    setCurrentAnalyticsSourceTrackingId,
    eventTriggerSource,
    isDemoOrder,
    dispatch,
    defaultPrimaryAction,
    fulfillmentPlanId,
    labelMetadata,
    sellerId,
    handleUpdateInsurancePreference,
  ]);

  const _onSelectedDutyOptionChange = useCallback(
    (selectedDutyOption) => {
      onSelectedDutyOptionChange(selectedDutyOption.additionalOptionValue.prepaidDuty, availableDutiesOptions, {
        shouldSkipRateRequest: true,
      });
    },
    [onSelectedDutyOptionChange, availableDutiesOptions],
  );

  return (
    <div className={classNames('button-group', style['button-container'], buttonContainerClassName)}>
      <LabelPurchaseButton
        availableDutiesOptions={availableDutiesOptions}
        canShowPrepayDutyAlert
        fulfillmentPlanIds={[fulfillmentPlanId]}
        labelPurchaseCallback={onDefaultAction}
        onSelectedDutyOptionChange={_onSelectedDutyOptionChange}
        otherShippingOptions={otherShippingOptions}
        trackingId={eventTriggerSource}
        useCswFallback={fulfillmentPlanId === RATE_BROWSER || fulfillmentPlanId === CUSTOMERS_CREATE_LABEL}
      >
        {({ onClick }) => (
          <Button
            className={classNames(style['button-styles'], buttonClassName)}
            disabled={disabled}
            disabledTooltipMessage={disabledTooltipMessage}
            isLightTheme={isLightTheme}
            onClick={onClick}
            theme={theme}
          >
            <div className={classNames(style['button-label'], buttonLabelClassName)}>
              <FormattedMessage {...defaultPrimaryActionBtnLabelTxt} />
            </div>
          </Button>
        )}
      </LabelPurchaseButton>

      {!printLabelButtonOnly && !isDemoOrder && (
        <DropdownMenu
          closeWhenChildrenClicked={false}
          disabled={disabled}
          dropdownMenuClassName={style['dropdown-menu']}
          menuPosition='bottom-right'
        >
          <DropdownMenuToggler className={classNames(style['drop-down-menu-button'], buttonClassName)} theme={theme} />
          {otherActions.map((primaryAction) => {
            const onAction = () => {
              guardAction(() => {
                dispatch(primaryAction.getActionFromFulfillmentPlanId(fulfillmentPlanId, labelMetadata));
                if (!isDemoOrder) {
                  handleUpdateInsurancePreference();
                }
              });
            };
            return (
              <OtherLabelActionButton
                key={primaryAction.id}
                disabled={disabled}
                eventTriggerSource={eventTriggerSource}
                onAction={onAction}
                primaryAction={primaryAction}
              />
            );
          })}
        </DropdownMenu>
      )}
    </div>
  );
};

MultiactionLabelButton.propTypes = {
  defaultPrimaryActionId: PropTypes.string,
  primaryActions: PropTypes.arrayOf(CswPrimaryActionPropType),
  disabled: PropTypes.bool,
  disabledTooltipMessage: PropTypes.string,
  theme: PropTypes.oneOf(ButtonThemeService.getButtonThemes()),
  buttonClassName: PropTypes.string,
  buttonContainerClassName: PropTypes.string,
  buttonLabelClassName: PropTypes.string,
  sourceId: PropTypes.string,
  isLightTheme: PropTypes.bool,
  guardAction: PropTypes.func,
};
